import React from "react";
import Pagination from "../../../components/Pagination";

/**
 * Desktop table view component for displaying products in a table layout
 * Includes sticky header and validation error handling
 */
const PriceTable = ({
  productRows,
  isCompactView,
  handleNewPriceChange,
  handleRemoveOfferPrice,
  handleToggleVisibility,
  priceErrors,
  currencySymbol,
  page,
  totalPages,
  onPageChange,
}) => {
  return (
    <div className="h-full flex flex-col bg-white rounded-lg shadow-sm overflow-hidden">
      {/* Table */}
      <div className="flex-1 overflow-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-black sticky top-0 z-10">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-bold text-white uppercase tracking-wider min-w-[200px]">
                Product Name
              </th>
              {!isCompactView && (
                <>
                  <th className="px-4 py-3 text-left text-xs font-bold text-white uppercase tracking-wider min-w-[150px]">
                    Category
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-bold text-white uppercase tracking-wider min-w-[150px]">
                    Subcategory
                  </th>
                </>
              )}
              <th className="px-4 py-3 text-left text-xs font-bold text-white uppercase tracking-wider min-w-[120px]">
                Current Price
              </th>
              <th className="px-4 py-3 text-left text-xs font-bold text-white uppercase tracking-wider min-w-[120px]">
                Offer Price
              </th>
              <th className="px-4 py-3 text-left text-xs font-bold text-white uppercase tracking-wider min-w-[150px]">
                New Price
              </th>
              <th className="px-4 py-3 text-left text-xs font-bold text-white uppercase tracking-wider min-w-[150px]">
                New Offer Price
              </th>
              <th className="px-4 py-3 text-center text-xs font-bold text-white uppercase tracking-wider min-w-[140px]">
                Remove Offer Price
              </th>
              <th className="px-4 py-3 text-center text-xs font-bold text-white uppercase tracking-wider min-w-[140px]">
                Product Availability
              </th>

            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {productRows.map((row, index) => {
              const errorKey = row.variantId
                ? `${row._id}-${row.variantId}`
                : row._id;
              const hasError = priceErrors[errorKey];

              return (
                <tr
                  key={`${row._id}-${row.variantId || index}`}
                  className="hover:bg-gray-50 transition-colors"
                >
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">
                    {row.productName}
                  </td>
                  {!isCompactView && (
                    <>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {row.categoryName}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {row.subcategoryName}
                      </td>
                    </>
                  )}
                  <td className="px-4 py-3 text-sm font-semibold text-green-600">
                    {currencySymbol}
                    {row.price}
                  </td>
                  <td className="px-4 py-3 text-sm font-semibold text-red-600">
                    {row.offerPrice != null && row.offerPrice !== "" && (
                      <>
                        {currencySymbol}
                        {row.offerPrice}
                      </>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="relative">
                      <span className="absolute left-3 top-2.5 text-gray-500 text-sm">
                        {currencySymbol}
                      </span>
                      <input
                        type="number"
                        value={row.newPrice}
                        onChange={(e) =>
                          handleNewPriceChange(
                            row._id,
                            row.variantId,
                            e.target.value,
                            "newPrice",
                          )
                        }
                        className="pl-8 pr-3 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      />
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="relative">
                      <span className="absolute left-3 top-2.5 text-gray-500 text-sm">
                        {currencySymbol}
                      </span>
                      <input
                        type="number"
                        value={row.newOfferPrice}
                        onChange={(e) =>
                          handleNewPriceChange(
                            row._id,
                            row.variantId,
                            e.target.value,
                            "newOfferPrice",
                          )
                        }
                        className={`pl-8 pr-3 py-2 w-full border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent ${hasError
                          ? "border-red-500 bg-red-50"
                          : "border-gray-300"
                          }`}
                      />
                      {hasError && (
                        <p className="text-xs text-red-500 mt-1">
                          Cannot exceed price
                        </p>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button
                      onClick={() =>
                        handleRemoveOfferPrice(row._id, row.variantId)
                      }
                      disabled={!row.offerPrice}
                      className={`text-xs px-3 py-1 rounded-lg font-medium transition-colors ${row.offerPrice
                          ? "bg-red-600 hover:bg-red-700 text-white"
                          : "bg-gray-100 text-gray-400 cursor-not-allowed"
                        }`}
                    >
                      Remove Offer
                    </button>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input 
                        type="checkbox" 
                        className="sr-only peer" 
                        checked={row.isVisible || false}
                        onChange={() => handleToggleVisibility(row._id, row.variantId, row.isVisible || false)}
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      <span className="ml-2 text-sm font-medium text-gray-900">
                        {row.isVisible ? "Enabled" : "Disabled"}
                      </span>
                    </label>
                  </td>

                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="border-t border-gray-100 px-6 py-4">
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            onPageChange={onPageChange}
          />
        </div>
      )}
    </div>
  );
};

export default PriceTable;
